import { useState } from "react";
import { Building2, ChevronDown, FileSpreadsheet, IdCard, LayoutDashboard } from "lucide-react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SupportButton from "@/components/base/SupportButton";
import PageMeta from "@/components/base/PageMeta";
import FirmaContablePricing from "@/components/feature/FirmaContablePricing";
import LeadForm from "./components/LeadForm";
import WhatsAppCta from "@/components/base/WhatsAppCta";
import { FIRMA_CTA_PRIMARY, FIRMA_WHATSAPP_URL } from "@/lib/plans";

const PILLARS = [
  {
    icon: LayoutDashboard,
    title: "Una sesión, todos tus clientes.",
    body: "Cambias de empresa con un clic. Sin credenciales sueltas, sin hojas de cálculo para saber quién facturó qué.",
  },
  {
    icon: IdCard,
    title: "Cada cliente con su propia identidad fiscal.",
    body: "Su RNC, su certificado P12, su numeración. La DGII recibe de cada uno por separado, aunque tú trabajes desde un solo lugar.",
  },
  {
    icon: FileSpreadsheet,
    title: "606, 607 y 608 sin armarlos a mano.",
    body: "Se pre-calculan por cliente con los comprobantes emitidos y recibidos. Los exportas en lote cuando toca el cierre.",
  },
  {
    icon: Building2,
    title: "Tus clientes trabajan, tú revisas.",
    body: "Le das acceso limitado a cada cliente para que facture su día a día. Ve solo su empresa. Tú ves todas.",
  },
];

const FAQS = [
  {
    q: "¿Los 450 e-CF son por cliente o en total?",
    a: "En total, como pool de la firma. Cada cliente adicional suma su dotación al pool. Si el mes cierra por encima, se factura un bloque de 50 por USD$6, así que el costo sigue siendo predecible.",
  },
  {
    q: "¿Cómo funciona la certificación ante la DGII?",
    a: "Cada RNC hace su propio proceso: postulación, set de pruebas y aprobación. Es requisito de la DGII y no se puede consolidar. El alta de USD$75 cubre ese acompañamiento por cliente.",
  },
  {
    q: "¿Y los certificados digitales?",
    a: "Cada cliente adquiere el suyo a nombre de su RNC. Nosotros lo cargamos vinculado únicamente a esa empresa, y el sistema valida que el RNC del comprobante coincida con el del certificado antes de firmar.",
  },
  {
    q: "¿Un cliente puede ver los datos de otro?",
    a: "No. El aislamiento es por reglas de registro de Odoo: el usuario de una empresa no ve transacciones, contactos ni reportes de otra. Tu equipo sí tiene visibilidad global.",
  },
  {
    q: "¿Qué pasa si me paso del pool sin haber comprado paquete?",
    a: "Se factura automáticamente en bloques de 50 a $6. Nunca cortamos la emisión de tus clientes por excedente.",
  },
  {
    q: "¿El excedente puede terminar costándome más que subir de tipo?",
    a: "No. La facturación de un cliente nunca supera el precio de su tipo siguiente; al llegar a ese punto lo subimos y te lo notificamos.",
  },
  {
    q: "¿Los paquetes se consumen antes que el excedente?",
    a: "Sí, siempre. Primero la cuota mensual, después el saldo de paquetes, y solo al final el excedente reactivo.",
  },
  {
    q: "¿Qué pasa si un cliente se va o crece?",
    a: "Extraemos su empresa a una base propia y lo migramos a un plan individual. USD$250 únicos, sin pérdida de histórico.",
  },
  {
    q: "¿Qué incluye el soporte?",
    a: "Infraestructura, conectividad con la DGII y errores del sistema. La capacitación y la configuración funcional de cada cliente van por bolsa de horas o quedan del lado de tu firma — que es justo lo que te permite cobrarlas como servicio propio.",
  },
];

export default function ContadoresPage() {
  const [darkMode, setDarkMode] = useState(false);
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <div className={darkMode ? "dark" : ""}>
      <PageMeta
        title="Firma Contable | emite.do"
        description="En tu Odoo, todas tus empresas, y el estado ante la DGII de cada RNC a la vista. Desde USD$150/mes con 3 clientes incluidos."
        robots="index, follow"
      />
      <div className={darkMode ? "bg-slate-950 text-white" : "bg-white text-slate-900"}>
        <Navbar darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} />
        <main>
          <section className="relative bg-slate-950 text-white overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-60" />
            <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-blue-800/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative max-w-4xl mx-auto px-4 md:px-6 pt-32 pb-20 text-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-slate-700 text-xs font-semibold uppercase tracking-widest text-slate-400 mb-6">
                Para firmas contables
              </span>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-5">
                Todo en un solo lugar!
              </h1>
              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-2">
                Deja de entrar y salir de un portal por cada cliente. Con Odoo, tendrás todas tus empresas, y el
                estado ante la DGII de cada RNC a la vista, en un solo lugar. Desde USD$150/mes con 3 clientes incluidos.
              </p>
              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Con Odoo como plataforma, esto es lo que te ofrecemos
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <WhatsAppCta href={FIRMA_WHATSAPP_URL}>{FIRMA_CTA_PRIMARY}</WhatsAppCta>
              </div>
            </div>
          </section>

          <section className="bg-white py-24 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Cuatro pilares de la firma</h2>
                <p className="text-slate-500">Operas varios RNC sin fragmentar tu operación.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {PILLARS.map((pillar, i) => (
                  <div key={pillar.title} className="bg-slate-50 border border-slate-100 rounded-2xl p-7">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl border border-slate-200">
                        <pillar.icon size={18} className="text-blue-800" />
                      </div>
                      <span className="text-xs font-bold text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2">{pillar.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{pillar.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="precios" className="bg-slate-50 py-24 px-4 md:px-6 scroll-mt-16">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-flex items-center px-3 py-1 rounded-full border border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">
                  Precio
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Firma Contable</h2>
                <p className="text-slate-500 text-base">
                  USD$150/mes con 3 clientes incluidos. Cada RNC extra suma capacidad, no letra chica.
                </p>
              </div>
              <FirmaContablePricing />
            </div>
          </section>

          <section className="bg-white py-24 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="text-sm text-slate-500 font-medium">FAQ</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Preguntas de firmas</h2>
                <p className="text-slate-500">Certificación, P12, aislamiento entre clientes y soporte.</p>
              </div>
              <div className="space-y-3">
                {FAQS.map((faq, i) => (
                  <div key={faq.q} className="border border-slate-100 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenIdx(openIdx === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-sm font-semibold text-slate-800 pr-4">{faq.q}</span>
                      <ChevronDown
                        size={16}
                        className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${openIdx === i ? "rotate-180" : ""}`}
                      />
                    </button>
                    <div className={`accordion-content ${openIdx === i ? "open" : ""}`}>
                      <div className="px-6 pb-5 pt-1">
                        <p className="text-sm text-slate-500 leading-relaxed">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-slate-50 py-24 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Cuéntanos de tu firma.</h2>
                <p className="text-slate-500">Nombre, email, teléfono, cartera y el software que usas hoy.</p>
              </div>
              <LeadForm />
            </div>
          </section>
        </main>
        <Footer darkMode={darkMode} />
        <SupportButton />
      </div>
    </div>
  );
}
